import {ModelBrandVisual} from '../ModelBrandVisual/ModelBrandVisual.js';

import type {ReactElement} from 'react';

const models = [
  {
    brand: 'openai',
    description: 'Develop an idea into a story, explore character motivations, and shape a sequence of shots with AI writing assistance.',
    modelVersion: 'GPT-6 Astra',
    name: 'OpenAI',
    role: 'Find your story.',
    status: 'AI writing · Paid credits',
    tools: ['Story development', 'Character ideas', 'Shot prompts']
  },
  {
    brand: 'claude',
    description: 'Refine scripts, build out character histories, and keep a long story consistent with careful, long-form reasoning.',
    modelVersion: 'Sonnet 5 · Opus 5',
    name: 'Claude',
    role: 'Think it through.',
    status: 'Writing & analysis · Paid credits',
    tools: ['Script refinement', 'Character histories', 'Continuity notes']
  },
  {
    brand: 'gemini',
    description: 'Explore visual references, research your world, and create or refine storyboard images in a creative conversation.',
    modelVersion: 'Gemini 3.8 Flash',
    name: 'Gemini',
    role: 'Explore the possibilities.',
    status: 'Creative assistant · Paid credits',
    tools: ['Reference analysis', 'Research', 'Image exploration']
  },
  {
    brand: 'seedance',
    description: 'Turn a reference image into moving footage. Direct the action, camera movement, duration, and sound from your shot brief.',
    modelVersion: 'Seedance 2.5',
    name: 'Seedance',
    role: 'Put it in motion.',
    status: 'Image to video · Paid credits',
    tools: ['Image to video', 'Motion direction', 'Audio']
  }
] as const;

export const CreativeModels = (): ReactElement => (
  <section aria-labelledby="creative-models-heading" className="mb-14 sm:mb-18">
    <div className="mb-6">
      <p className="text-xs font-medium tracking-[.18em] text-sky-200 uppercase">The models behind your next idea</p>
      <h2 className="mt-3 text-3xl tracking-tight sm:text-4xl" id="creative-models-heading">Imagine. Develop. <span className="text-violet-300">Generate.</span></h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300">Work with OpenAI, Claude, Gemini, and Seedance 2.5 in one creative workflow, from your first idea to the moving image.</p>
    </div>
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {models.map((model) => (
        <article className="overflow-hidden rounded-3xl border border-violet-300/20 bg-[#151419]" key={model.name}>
          <ModelBrandVisual brand={model.brand} modelVersion={model.modelVersion} />
          <div className="p-6">
            <p className="text-xs text-sky-200">{model.status}</p>
            <p className="mt-5 text-xl text-violet-200">{model.role}</p>
            <p className="mt-3 text-sm leading-6 text-zinc-300">{model.description}</p>
            <ul className="mt-5 flex flex-wrap gap-2">{model.tools.map((tool) => <li className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs leading-5 text-zinc-200" key={tool}>{tool}</li>)}</ul>
          </div>
        </article>
      ))}
    </div>
    <p className="mt-4 text-xs leading-6 text-zinc-400">Illustrative concept imagery. AI tools use paid credits and require provider connections configured for your studio.</p>
  </section>
);
